import isEqual from 'lodash/isEqual'
import { useState, useCallback, useMemo } from 'react'
// @mui
import Card from '@mui/material/Card'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import CircularProgress from '@mui/material/CircularProgress'
import { alpha } from '@mui/material/styles'

// routes
import { paths } from 'src/routes/paths'
import { useRouter } from 'src/routes/hook'
// components
import Iconify from 'src/components/iconify'
import { useSettingsContext } from 'src/components/settings'

import Datagrid from 'src/components/datagrid'

//
import { Helmet } from 'react-helmet-async'
import axiosInstance from 'src/utils/axios'
import { useSnackbar } from 'notistack'
import { useBoolean } from 'src/hooks/use-boolean'
import { masks } from 'src/utils/masks'
import useCard, { fetchAllCards } from 'src/service/card.service'
import { ICardTableFilterValue } from 'src/types/card'
import UserTableRow from './table-user/user-table-row'
import UserTableToolbar from './table-user/user-table-toolbar'
import UserTableFiltersResult from './table-user/user-table-filters-result'
import UserCreateEditForm from './user-create-edit-view'
import { exportCardsToPdf, exportCardsToCsv } from './export-cards'

export const USER_STATUS_OPTIONS = [
  { value: 'true', label: 'Ativos' },
  { value: 'false', label: 'Desativados' },
]

const _roles = [
  { value: 'admin', label: 'Admin' },
  { value: 'user', label: 'Usuário' },
  { value: 'franchisee', label: 'Franqueado' },
]

const STATUS_OPTIONS = [{ value: 'all', label: 'Todos' }, ...USER_STATUS_OPTIONS]

const TABLE_HEAD = [
  { id: 'card', label: 'Cartão' },
  { id: 'event', label: 'Evento', width: 180 },
  { id: 'balance', label: 'Saldo', width: 140 },
  { id: 'totalTransactions', label: 'Movimentações', width: 130 },
  { id: '', width: 140 },
]

const defaultPagination = {
  page: 1,
  perPage: 8,
  sort: {
    createdAt: 'desc',
  },
}
const defaultFilters = {
  search: '',
  role: [],
  active: 'all',
}
export default function TransactionList() {
  const [pagination, setPagination] = useState(defaultPagination)
  const [filters, setFilters] = useState(defaultFilters)

  const { isLoading, isFetching, data, refetch } = useCard(pagination, filters)

  const settings = useSettingsContext()

  const { enqueueSnackbar } = useSnackbar()

  const router = useRouter()

  const quickEdit = useBoolean()

  const canReset = !isEqual(defaultFilters, filters)

  const notFound = (!data?.data.length && canReset) || !data?.data.length

  // Saldo total de todos os cartões do filtro (calculado no backend).
  const totalBalance = data?.totalBalance || 0

  const isExporting = useBoolean()
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null)

  const filterLabel = useMemo(() => {
    const parts: string[] = []
    const activeLabel = STATUS_OPTIONS.find((o) => o.value === filters.active)?.label
    if (filters.active !== 'all' && activeLabel) parts.push(activeLabel)
    if (filters.search) parts.push(`Busca: "${filters.search}"`)
    return parts.join(' · ')
  }, [filters])

  const handleExport = useCallback(
    async (kind: 'pdf' | 'csv') => {
      setExportAnchor(null)
      try {
        isExporting.onTrue()
        const cards = await fetchAllCards(filters)
        const meta = { totalBalance, filterLabel }
        if (kind === 'pdf') exportCardsToPdf(cards, meta)
        else exportCardsToCsv(cards, meta)
      } catch (error) {
        enqueueSnackbar('Erro ao exportar cartões.', { variant: 'error' })
      } finally {
        isExporting.onFalse()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters, totalBalance, filterLabel],
  )

  const handleFilters = useCallback((name: string, value: ICardTableFilterValue) => {
    setPagination((prev) => ({ ...prev, page: 1 }))
    setFilters((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }, [])

  const handleDeleteRow = useCallback(
    async (id: string) => {
      try {
        await axiosInstance.delete(`/card/${id}`)

        enqueueSnackbar('Cartão removido com sucesso !')
        refetch()
      } catch (error) {
        if (error?.message) {
          enqueueSnackbar(error?.message, { variant: 'error' })
        } else {
          enqueueSnackbar('Erro ao remover cartão !', { variant: 'error' })
        }
      }
    },
    [enqueueSnackbar, refetch],
  )

  const handleEditRow = useCallback(
    (id: string) => {
      router.push(paths.dashboard.transaction.edit(id))
    },
    [router],
  )

  const handleFilterStatus = useCallback(
    (event: React.SyntheticEvent, newValue: string) => {
      handleFilters('active', newValue)
    },
    [handleFilters],
  )

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters)
  }, [])

  return (
    <>
      <Helmet>
        <title>Cashless - Cartões</title>
      </Helmet>

      <Container maxWidth={settings.themeStretch ? false : 'lg'}>
        {/* Header compacto */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 2,
            px: { xs: 2, sm: 2.5 },
            py: { xs: 1.5, sm: 2 },
            mb: { xs: 2, md: 2.5 },
            color: 'white',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent='space-between'
            spacing={1.5}
          >
            <Stack direction='row' alignItems='center' spacing={1.5}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  bgcolor: 'rgba(255,255,255,0.18)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Iconify icon='solar:card-bold' width={22} />
              </Box>
              <Box>
                <Typography variant='subtitle1' fontWeight={700} sx={{ fontSize: { xs: 16, sm: 18 } }}>
                  Cashless
                </Typography>
                <Typography variant='caption' sx={{ opacity: 0.9, fontSize: 12 }}>
                  Gestão de cartões e movimentações
                </Typography>
              </Box>
            </Stack>

            <Stack direction='row' spacing={1}>
              <Button
                onClick={(e) => setExportAnchor(e.currentTarget)}
                variant='contained'
                color='inherit'
                size='small'
                disabled={isExporting.value}
                startIcon={
                  isExporting.value ? (
                    <CircularProgress size={16} color='inherit' />
                  ) : (
                    <Iconify icon='solar:export-bold' />
                  )
                }
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                }}
              >
                Exportar
              </Button>

              <Button
                onClick={() => quickEdit.onTrue()}
                variant='contained'
                color='inherit'
                size='small'
                startIcon={<Iconify icon='mingcute:add-line' />}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                }}
              >
                Novo cartão
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Menu
          anchorEl={exportAnchor}
          open={Boolean(exportAnchor)}
          onClose={() => setExportAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={() => handleExport('pdf')}>
            <ListItemIcon>
              <Iconify icon='solar:file-text-bold' width={20} />
            </ListItemIcon>
            Exportar PDF
          </MenuItem>
          <MenuItem onClick={() => handleExport('csv')}>
            <ListItemIcon>
              <Iconify icon='vscode-icons:file-type-excel' width={20} />
            </ListItemIcon>
            Exportar Excel
          </MenuItem>
        </Menu>

        {/* KPIs */}
        <Box
          display='grid'
          gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)' }}
          gap={1.5}
          sx={{ mb: 2.5 }}
        >
          <KpiCard
            icon='solar:card-2-bold'
            color='#6366f1'
            label='Total de cartões'
            value={String(data?.total || 0)}
          />
          <KpiCard
            icon='solar:wallet-money-bold'
            color='#10b981'
            label='Saldo total'
            value={masks.currencyAllPlatforms(totalBalance)}
          />
        </Box>

        <Card>
          <Tabs
            value={filters.active}
            onChange={handleFilterStatus}
            sx={{
              px: 2.5,
              boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
            }}
          >
            {STATUS_OPTIONS.map((tab) => (
              <Tab key={tab.value} iconPosition='end' value={tab.value} label={tab.label} />
            ))}
          </Tabs>
          <UserTableToolbar filters={filters} onFilters={handleFilters} roleOptions={_roles} />

          {canReset && (
            <UserTableFiltersResult
              filters={filters}
              onFilters={handleFilters}
              onResetFilters={handleResetFilters}
              results={data?.data.length || 0}
              sx={{ p: 2.5, pt: 0 }}
            />
          )}

          <Datagrid
            data={data?.data || []}
            notFound={notFound}
            isLoading={isLoading || isFetching}
            headLabel={TABLE_HEAD}
            refetch={refetch}
            total={data?.total || 0}
            setPagination={setPagination}
            pagination={pagination}
            rowsPerPageOptions={[8, 32, 100, 500, 1000, 5000]}
            noSelection
            handleDeleteRows={() => {}}
            ItemContent={UserTableRow}
            handleDeleteRow={handleDeleteRow}
            handleEditRow={handleEditRow}
          />
        </Card>
      </Container>
      <UserCreateEditForm
        onRefleshTable={() => refetch()}
        open={quickEdit.value}
        onClose={quickEdit.onFalse}
      />
    </>
  )
}

// ----------------------------------------------------------------------

function KpiCard({
  icon,
  color,
  label,
  value,
}: {
  icon: string
  color: string
  label: string
  value: string
}) {
  return (
    <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
      <Stack direction='row' alignItems='center' spacing={1.5}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1.25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${color}1a`,
            color,
          }}
        >
          <Iconify icon={icon} width={20} />
        </Box>
        <Box>
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 }}
          >
            {label}
          </Typography>
          <Typography variant='h6' fontWeight={700} sx={{ fontSize: 18 }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  )
}
